const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

export const API = `${BACKEND_URL}/api`;

export const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Thin wrapper for admin calls. Throws an Error carrying the backend's own message
 * so callers can pass it straight to a toast — the API returns `detail` (FastAPI)
 * and occasionally `message`.
 */
export const adminFetch = async (path, { method = 'GET', body, signal } = {}) => {
  /*
   * FormData must pass through untouched.
   *
   * This used to JSON.stringify every body unconditionally, which turns a
   * FormData into the string "{}" — so a multipart request arrived at the
   * server with an empty JSON object and no fields at all. The backend then
   * correctly complained about whichever required field it checked first,
   * which read like a validation bug on its side rather than a body that never
   * left the browser.
   *
   * Content-Type is also deliberately omitted for FormData: the browser has to
   * set it itself so it can append the multipart boundary. Setting it by hand
   * produces a body the server cannot split.
   */
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  const res = await fetch(`${API}${path}`, {
    method,
    headers: {
      ...authHeaders(),
      ...(body && !isFormData ? { 'Content-Type': 'application/json' } : {})
    },
    credentials: 'include',
    body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
    signal
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.detail || err.message || `Request failed (${res.status})`);
    error.status = res.status;
    throw error;
  }

  // 204 and empty bodies are valid successes.
  return res.status === 204 ? null : res.json().catch(() => null);
};

/**
 * List endpoints are not uniform about their array key, and none of them declare a
 * response model — so read defensively rather than assuming one shape.
 */
export const listOf = (data, ...keys) => {
  if (Array.isArray(data)) return data;
  for (const k of keys) if (Array.isArray(data?.[k])) return data[k];
  return [];
};

export const formatPrice = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2
  }).format(Number(value) || 0);

export const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit'
  });
};

export const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: '2-digit' });
};

export const timeAgo = (value) => {
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return '—';
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days < 30 ? `${days}d ago` : formatDate(value);
};

/** Windowed page numbers with ellipses, matching the other admin tables. */
export const pageNumbers = (page, totalPages, delta = 2) => {
  if (totalPages <= 1) return [1];
  const pages = [1];
  const left = Math.max(2, page - delta);
  const right = Math.min(totalPages - 1, page + delta);
  if (left > 2) pages.push('...');
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < totalPages - 1) pages.push('...');
  if (!pages.includes(totalPages)) pages.push(totalPages);
  return pages;
};
